/**
 * The API-gateway section of the Dynamics board (L1): how API Management
 * answered the calls it carried into F&O, which of its outbound calls failed,
 * and which of its operations are slowest.
 *
 * ## Whose numbers these are
 *
 * The backend's request queries name no application — `REQUEST_RESULT_CODE_QUERY`
 * and `FAILED_DEPENDENCY_QUERY` read *every* row in `AppRequests` /
 * `AppDependencies`, with no `AppRoleName` filter anywhere. Measured 22 Aug
 * 2026, every one of those rows belonged to the `NO-APIM-NAX West Europe` API
 * Management instance that sits in front of F&O: 46,856 requests and 46,520
 * HTTP dependencies over seven days, all targeting
 * `nax365.operations.eu.dynamics.com`, p50 175 ms, p95 673 ms, 21 failures.
 *
 * So this section is the **gateway's** traffic, and it now says so. It used to
 * be headed "F&O request health", which is a claim about the ERP itself that
 * this data has never carried: a label wider than its evidence.
 *
 * The dated sentence in the section lead is a workaround, not the fix. The
 * durable version is a payload that reports which applications answered — a
 * per-`AppRoleName` count on `FoRequests` — so the caption is computed from the
 * window rather than asserted from a one-off check. That is an API change and
 * is written up in the handover instead of made here.
 *
 * ## Why there is no application-versus-database split
 *
 * A `SegmentBar` used to sit beside the donut splitting request time into
 * application and SQL. Removed 22 Aug 2026: the workspace holds **zero**
 * `SQL`-type dependency rows, because F&O emits no SQL dependency telemetry at
 * all. `REQUEST_SQL_SPLIT_QUERY` coalesces the missing SQL duration to `0`, so
 * the bar could only ever draw 100 % application / 0 % database, for ever, on
 * every window — and a permanent 100 % is read as a measurement. It is replaced
 * by one sentence in the lead saying where database timing actually has to come
 * from (a production trace, which is how the root-cause work got it).
 *
 * The same defect ran through the ranking grid's `Mean SQL` column, which was a
 * column of `0 ms`; it is gone for the same reason. The payload still carries
 * `avg_sql_ms` / `avg_app_ms` / `sql_calls` — nothing was removed from the API,
 * only the rendering of a number that cannot be measured.
 *
 * Form choices, per §2:
 *
 * - **Result codes are a donut** — genuine part-to-whole over one window, held
 *   to four labelled segments plus a folded "Other codes" remainder, with the
 *   total in the hole. They wear the *status* palette rather than the
 *   categorical ramp, because a result code is a verdict: `500` is broken and
 *   `200` is not, and pairing each with an icon keeps it off colour alone.
 * - **Failing outbound calls are a sorted horizontal bar** of failures — a true
 *   zero-based magnitude comparison across categories.
 *
 * Privacy is structural here rather than enforced by this file: a dependency is
 * identified by `dependency_type` and `target` and nothing else, because the
 * name Application Insights records is the statement for a SQL dependency and
 * the full URL for an HTTP one, and both embed the literal data values they ran
 * against. There is no field on `FoFailedDependency` that could carry it, so
 * there is nothing to redact.
 */

import { Chart } from "@norican/dataviz";
import { Icon } from "@norican/icons";
import { Badge, DataGrid, EmptyState, Statistic, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { FoFailedDependency, FoRequestPerformance, FoRequests, FoResultCode } from "@/lib/api";
import { DrillLink, useDrillNavigate } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { DYNAMICS_REQUESTS_PATH, REQUESTS_ANCHOR, RESULT_CODE_PARAM } from "./params";
import {
  dependencyLabel,
  formatCount,
  formatMs,
  formatPercent,
  formatWindow,
  resultCodeStatus,
  suppressionCaption,
  tooltipValue,
} from "./status";

/** How many result codes keep their own donut segment before the tail folds. */
const DONUT_SEGMENTS = 4;

/** How many rows the L1 "worst" grids and charts show — the rest live at L2. */
const WORST_LIMIT = 5;

/** The folded donut remainder's key. Distinct from the privacy fold's "Other". */
const OTHER_CODES = "Other codes";

interface DonutSlice {
  key: string;
  requests: number;
  color: string;
  /** True for the legibility fold — not the k-anonymity fold. */
  folded: boolean;
}

interface RequestsSectionProps {
  requests: FoRequests;
}

export function RequestsSection({ requests }: RequestsSectionProps) {
  const navigateDrill = useDrillNavigate();
  const windowLabel = formatWindow(requests.window);
  const staleAfterMs = requests.cache_ttl_sec * 2000;

  const codes = [...requests.result_codes].sort((a, b) => b.requests - a.requests);
  const totalRequests = codes.reduce((sum, row) => sum + row.requests, 0);
  const head = codes.slice(0, DONUT_SEGMENTS);
  const tail = codes.slice(DONUT_SEGMENTS);
  const donutSlices: DonutSlice[] = [
    ...head.map((row) => ({
      key: row.result_code,
      requests: row.requests,
      color: resultCodeStatus(row.result_code).color,
      folded: false,
    })),
    ...(tail.length > 0
      ? [
          {
            key: OTHER_CODES,
            requests: tail.reduce((sum, row) => sum + row.requests, 0),
            color: "var(--norican-bg-muted)",
            folded: true,
          },
        ]
      : []),
  ].filter((slice) => slice.requests > 0);

  const dependencies = [...requests.failed_dependencies].sort(
    (a, b) => b.failures - a.failures || b.calls - a.calls,
  );
  const worstDependencies = dependencies.slice(0, WORST_LIMIT).map((row) => ({
    ...row,
    label: dependencyLabel(row.dependency_type, row.target),
  }));

  const labelledRequests = requests.worst_requests.filter((row) => !row.is_other);
  const otherRequest = requests.worst_requests.find((row) => row.is_other);
  const worstRequests = [
    ...labelledRequests.slice(0, WORST_LIMIT),
    ...(otherRequest ? [otherRequest] : []),
  ];

  return (
    <section id={REQUESTS_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      {/* The source statement, not decoration: everything below is the
          gateway's traffic, and the panel titles alone cannot carry that. */}
      <div className="flex flex-col gap-2">
        <Typography variant="body-sm" tone="muted">
          These are <strong className="text-fg">API Management&apos;s</strong> numbers, not
          F&amp;O&apos;s own. The queries behind this section select no application: they read every
          request row in the workspace, and on 22 August 2026 every one of those rows came from the{" "}
          <code className="text-fg">NO-APIM-NAX</code> gateway that sits in front of F&amp;O. What
          is counted here is the traffic that gateway carried — how F&amp;O answered it, how long
          the answer took, and which of the gateway&apos;s own outbound calls failed. It does not
          cover a person&apos;s form load, which is a browser page view counted on Forms, and it
          says nothing about what F&amp;O did internally.
        </Typography>
        <Typography variant="body-sm" tone="muted">
          There is no application-versus-database split here because F&amp;O emits no SQL dependency
          telemetry at all, so database time has to come from a production trace taken while the
          slowness is happening.
        </Typography>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard<FoResultCode>
          title="Result codes the gateway returned"
          caption={
            codes.length === 0
              ? "Not measured"
              : `${formatCount(totalRequests)} calls, ${windowLabel}${
                  tail.length > 0
                    ? ` · the ${tail.length} smallest codes are folded into "${OTHER_CODES}" for legibility — every code is in the data table`
                    : ""
                }`
          }
          since={requests.collected_at}
          staleAfterMs={staleAfterMs}
          tableTwin={{
            id: "fo-result-codes",
            ariaLabel: "Request volume and failures per result code",
            data: codes,
            getRowId: (row) => row.result_code,
            columns: [
              {
                id: "code",
                header: "Result code",
                accessor: (row) => row.result_code,
                cell: (row) => (
                  <Badge tone={resultCodeStatus(row.result_code).tone} variant="subtle" size="xs">
                    {resultCodeStatus(row.result_code).label}
                  </Badge>
                ),
              },
              {
                id: "requests",
                header: "Requests",
                align: "end",
                accessor: (row) => row.requests,
                cell: (row) => <span className="tabular-nums">{formatCount(row.requests)}</span>,
              },
              {
                id: "failed",
                header: "Failed",
                align: "end",
                accessor: (row) => row.failed,
                cell: (row) => <span className="tabular-nums">{formatCount(row.failed)}</span>,
              },
              {
                id: "share",
                header: "Share",
                align: "end",
                accessor: (row) => row.share_pct,
                cell: (row) => <span className="tabular-nums">{formatPercent(row.share_pct)}</span>,
              },
              {
                id: "p95",
                header: "P95",
                align: "end",
                accessor: (row) => row.p95_duration_ms,
                cell: (row) => (
                  <span className="tabular-nums">{formatMs(row.p95_duration_ms)}</span>
                ),
              },
            ],
          }}
        >
          {donutSlices.length === 0 ? (
            <EmptyState
              size="sm"
              title="No request telemetry in this window"
              description="The workspace held no request rows at all. No result code is not the same as every call succeeding."
            />
          ) : (
            <div className="flex flex-col gap-3">
              <div className="relative">
                <Chart.Container
                  config={Object.fromEntries(
                    donutSlices.map((slice) => [
                      slice.key,
                      { label: slice.key, color: slice.color },
                    ]),
                  )}
                  className="h-64"
                  aria-label={`Share of API Management calls by result code, ${windowLabel}`}
                >
                  <Chart.PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Chart.Tooltip
                      content={
                        <Chart.TooltipContent
                          formatter={(value) => `${formatCount(tooltipValue(value))} calls`}
                          hideLabel
                        />
                      }
                    />
                    <Chart.Pie
                      {...Chart.seriesDefaults}
                      data={donutSlices}
                      dataKey="requests"
                      nameKey="key"
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="85%"
                      paddingAngle={2}
                      cornerRadius={3}
                      stroke="var(--norican-surface)"
                      strokeWidth={2}
                      cursor="pointer"
                      onClick={(_, index) => {
                        const slice = donutSlices[index];
                        if (slice) {
                          navigateDrill(
                            DYNAMICS_REQUESTS_PATH,
                            slice.folded
                              ? { [RESULT_CODE_PARAM]: tail.map((row) => row.result_code) }
                              : { [RESULT_CODE_PARAM]: slice.key },
                          );
                        }
                      }}
                    >
                      {donutSlices.map((slice) => (
                        <Chart.Cell key={slice.key} fill={slice.color} />
                      ))}
                    </Chart.Pie>
                  </Chart.PieChart>
                </Chart.Container>
                {/* The hole carries the total, per the DS donut recipe. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                >
                  <Statistic
                    label="Calls"
                    value={formatCount(totalRequests)}
                    size="lg"
                    className="text-center"
                  />
                </div>
              </div>
              {/* Result codes are a status scale, so each carries its icon. */}
              <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
                {donutSlices.map((slice) => {
                  const status = resultCodeStatus(slice.key);
                  return (
                    <span key={slice.key} className="inline-flex items-center gap-1.5">
                      <Icon icon={status.icon} size="xs" />
                      {slice.folded ? OTHER_CODES : status.label} ({formatCount(slice.requests)})
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </ChartCard>

        <ChartCard<FoFailedDependency>
          title="Failing outbound calls"
          caption={
            dependencies.length === 0
              ? "Not measured"
              : `Worst ${Math.min(WORST_LIMIT, dependencies.length)} of ${formatCount(
                  dependencies.length,
                )} · identified by type and target only, never by name`
          }
          since={requests.collected_at}
          staleAfterMs={staleAfterMs}
          tableTwin={{
            id: "fo-dependencies",
            ariaLabel: "Failing outbound calls by dependency type and target",
            data: dependencies,
            getRowId: (row) => dependencyLabel(row.dependency_type, row.target),
            columns: [
              { id: "type", header: "Type", accessor: (row) => row.dependency_type },
              { id: "target", header: "Target", accessor: (row) => row.target },
              {
                id: "calls",
                header: "Calls",
                align: "end",
                accessor: (row) => row.calls,
                cell: (row) => <span className="tabular-nums">{formatCount(row.calls)}</span>,
              },
              {
                id: "failures",
                header: "Failures",
                align: "end",
                accessor: (row) => row.failures,
                cell: (row) => <span className="tabular-nums">{formatCount(row.failures)}</span>,
              },
              {
                id: "rate",
                header: "Failure rate",
                align: "end",
                accessor: (row) => row.failure_pct,
                cell: (row) => (
                  <span className="tabular-nums">{formatPercent(row.failure_pct)}</span>
                ),
              },
              {
                id: "p95",
                header: "P95",
                align: "end",
                accessor: (row) => row.p95_duration_ms,
                cell: (row) => (
                  <span className="tabular-nums">{formatMs(row.p95_duration_ms)}</span>
                ),
              },
            ],
          }}
        >
          {worstDependencies.length === 0 ? (
            <EmptyState
              size="sm"
              title="No outbound call reported a failure"
              description="Either nothing failed, or nothing was measured — the panel state above says which."
            />
          ) : (
            <Chart.Container
              config={{ failures: { label: "Failed calls", color: "var(--norican-danger)" } }}
              className="h-64"
              aria-label={`Failed outbound calls by dependency type and target, ${windowLabel}, worst first`}
            >
              <Chart.BarChart
                data={worstDependencies}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
                <Chart.YAxis {...Chart.axisProps} type="category" dataKey="label" width={200} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      rows={(datum: FoFailedDependency) => [
                        { label: "Failures", value: formatCount(datum.failures) },
                        { label: "Calls", value: formatCount(datum.calls) },
                        { label: "Failure rate", value: formatPercent(datum.failure_pct) },
                        { label: "P95", value: formatMs(datum.p95_duration_ms) },
                      ]}
                    />
                  }
                />
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="failures"
                  fill="var(--color-failures)"
                  radius={[0, 3, 3, 0]}
                  cursor="pointer"
                  onClick={() => navigateDrill(DYNAMICS_REQUESTS_PATH)}
                />
              </Chart.BarChart>
            </Chart.Container>
          )}
        </ChartCard>
      </div>

      <PanelCard
        title="Slowest gateway operations"
        caption={
          // Rendered at every list length — this is the only path on the board
          // to /dynamics/requests, which carries the full result-code and
          // outbound-call tables as well as the whole ranking (§4.1). The
          // wording, not the link, is what changes when the grid already shows
          // every row.
          labelledRequests.length > WORST_LIMIT ? (
            <DrillLink
              to={DYNAMICS_REQUESTS_PATH}
              className="text-accent text-sm font-medium hover:underline"
            >
              View all {formatCount(labelledRequests.length)} ranked operations →
            </DrillLink>
          ) : (
            <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span>{`${formatCount(labelledRequests.length)} labelled operation names ranked`}</span>
              <DrillLink
                to={DYNAMICS_REQUESTS_PATH}
                className="text-accent text-sm font-medium hover:underline"
              >
                Open the full ranking →
              </DrillLink>
            </span>
          )
        }
      >
        <div className="flex flex-col gap-3">
          {worstRequests.length === 0 ? (
            <EmptyState
              size="sm"
              title="No operation was slow enough to rank"
              description="No request telemetry reached the ranking query in this window."
            />
          ) : (
            <DataGrid<FoRequestPerformance>
              data={worstRequests}
              getRowId={(row) => row.request_name}
              density="compact"
              aria-label={`The ${WORST_LIMIT} slowest API Management operations by P95 duration`}
              columns={[
                {
                  id: "request",
                  header: "Operation",
                  accessor: (row) => row.request_name,
                  cell: (row) =>
                    row.is_other ? (
                      <span className="text-fg-muted inline-flex items-center gap-2 text-sm italic">
                        Other
                        <Badge tone="neutral" variant="outline" size="xs">
                          folded cohort
                        </Badge>
                      </span>
                    ) : (
                      <span className="text-fg text-sm font-medium">{row.request_name}</span>
                    ),
                },
                {
                  id: "requests",
                  header: "Executions",
                  align: "end",
                  accessor: (row) => row.requests,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.requests)}</span>,
                },
                {
                  id: "failed",
                  header: "Failed",
                  align: "end",
                  accessor: (row) => row.failed,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.failed)}</span>,
                },
                {
                  id: "p95",
                  header: "P95",
                  align: "end",
                  accessor: (row) => row.p95_duration_ms,
                  cell: (row) => (
                    <span className="tabular-nums">{formatMs(row.p95_duration_ms)}</span>
                  ),
                },
                // No `Mean SQL` column: `avg_sql_ms` is a coalesced zero on
                // every row, because no SQL dependency telemetry exists to
                // subtract. A column of `0 ms` is read as a measurement.
              ]}
            />
          )}
          <Typography variant="body-sm" tone="muted">
            {suppressionCaption(requests.suppression, "requests")}
          </Typography>
        </div>
      </PanelCard>
    </section>
  );
}
